/* eslint-disable class-methods-use-this */
import React from 'react';
import I18n from 'i18n-js';
import gql from 'graphql-tag';

import {
  getExpeditedShippingPrice,
  getCombinedShippingPrice,
  getStandardShippingMethod,
} from '@reverbdotcom/commons/src/components/listing_shipping_display';
import { RCAlertBox, RCToggledContent, RCTooltip } from '@reverbdotcom/cadence/components';
import CollapsingList from '@reverbdotcom/commons/src/components/collapsing_list';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import {
  Item2ShopShippingPoliciesData,
  core_apimessages_ShippingPrice,
} from '../schemas/rql';
import { chain, keyBy } from 'lodash';

interface IProps {
  listing: Item2ShopShippingPoliciesData.Fragment;
}

interface ShippingPrice {
  [key: string]: core_apimessages_ShippingPrice;
}

type ShippingPrices = ShippingPrice[];

export const Item2ShopShippingPoliciesFragment = gql`
  fragment Item2ShopShippingPoliciesData on Listing {
    _id
    shipping {
      localPickup
      shippingPrices {
        shippingMethod
        rate {
          display
        }
      }
    }
    shop {
      _id
      shippingPolicy
      address {
        displayLocation
      }
    }
    allShippingPrices {
      shippingPrices {
        shippingMethod
        rate {
          display
        }
        shippingRegionCode
      }
    }
  }
`;

function getShippingRateText(shippingPrice: ShippingPrice) {
  if (shippingPrice.EXPEDITED_SHIPPING) {
    return I18n.t('discovery.listingShippingPolicies.standardAndExpeditedRate', {
      rate: shippingPrice.SHIPPED.rate.display,
      expeditedRate: shippingPrice.EXPEDITED_SHIPPING.rate.display,
    });
  }
  return shippingPrice.SHIPPED?.rate?.display || I18n.t('discovery.listingShippingPolicies.calculatedAtCheckout');
}

export default class ListingShippingPolicies extends React.PureComponent<IProps, null> {
  renderShippingRates() {
    const shippingPrices = this.shippingPricesByRegionCode();
    if (!shippingPrices.length) return null;

    return (
      <table className="basic-table">
        <thead>
          <tr>
            <td>
              {I18n.t('discovery.listingShippingPolicies.region')}
            </td>
            <td>
              {I18n.t('discovery.listingShippingPolicies.rate')}
            </td>
          </tr>
        </thead>
        <CollapsingList
          renderAsTableBody
        >
          {shippingPrices.map(this.renderShippingPrice)}
        </CollapsingList>
      </table>
    );
  }

  renderShippingPrice(price: ShippingPrice, idx) {
    return (
      <React.Fragment key={idx}>
        <td>
          {I18n.t(`discovery.shippingRegions.${price.SHIPPED.shippingRegionCode}`)}
        </td>
        <td>
          {getShippingRateText(price)}
        </td>
      </React.Fragment>
    );
  }

  shippingPricesByRegionCode(): ShippingPrices {
    const { shippingPrices } = this.props.listing.allShippingPrices;

    return chain(shippingPrices)
      .groupBy('shippingRegionCode')
      .values()
      .map(v => keyBy(v, 'shippingMethod'))
      .value();
  }

  standardShippingDisplay(shippingPrice: core_apimessages_ShippingPrice) {
    if (shippingPrice.rate) {
      return I18n.t('discovery.listingShippingPolicies.standardShipping', {
        rate: shippingPrice.rate.display,
      });
    }

    return I18n.t('discovery.listingShippingPolicies.estimatedStandardShipping');
  }

  render() {
    const { shipping, shop } = this.props.listing;
    if (!shipping) { return null; }

    const standardShipping = getStandardShippingMethod(this.props.listing);

    return (
      <div>
        {!!shipping.shippingPrices?.length &&
          <>
            <p
              data-display-location
              className="mb-1 size-90"
            >
              {I18n.t('discovery.listingShippingPolicies.shipsFrom', { location: shop.address.displayLocation })}
            </p>
            <ul className="cms-ul size-90">
              {shipping.localPickup &&
                <li
                  data-local-pickup
                >
                  {I18n.t('discovery.listingShippingPolicies.localPickup')}
                </li>
              }
              {standardShipping &&
                <li
                  data-standard-shipping
                >
                  {this.standardShippingDisplay(standardShipping)}
                </li>
              }
              {getExpeditedShippingPrice(this.props.listing)?.display &&
                <li>
                  {I18n.t('discovery.listingShippingPolicies.expeditedShipping', {
                    rate: getExpeditedShippingPrice(this.props.listing).display,
                  })}
                </li>
              }
              {getCombinedShippingPrice(this.props.listing)?.display &&
                <li>
                  <RCTooltip
                    text={I18n.t('commons.howToCombinedShippingTooltip')}
                    type="icon"
                  >
                    {I18n.t('discovery.listingShippingPolicies.combinedShipping', {
                      rate: getCombinedShippingPrice(this.props.listing).display,
                    })}
                  </RCTooltip>
                </li>
              }
            </ul>
          </>
        }
        <RCAlertBox
          type="plain"
          small
        >
          <SanitizedRender
            data-shipping-policy
            html={shop.shippingPolicy}
            htmlTag="div"
            truncationLimit={400}
            removeNonBreakingSpaces
          />
        </RCAlertBox>
        <div
          className="mt-1 plr-1 bdt-1 bdb-1 bd-primary"
        >
          <RCToggledContent
            heading={I18n.t('discovery.listingShippingPolicies.allRates')}
            small
            id="listing-shipping-policies"
          >
            {this.renderShippingRates()}
          </RCToggledContent>
        </div>
      </div>
    );
  }
}
